"use client";

import Link from "next/link";
import { useState, useEffect, useMemo, useCallback } from "react";
/* navbar entrance animation is CSS-only (see .nav-in in globals.css) */
import { useTranslations } from "next-intl";
import { useLocale } from "@/app/components/LocaleProvider";
import { getBaseLocale, getPathWithLocale } from "@/lib/i18n";
import { usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import LoginPopup from "./LoginPopup";
import RegisterPopup from "./RegisterPopup";
import SideMenu from "./SideMenu";
import LangSwitcher from "./LangSwitcher";

export default function Navbar() {
  const commonT = useTranslations("Common");
  const navbarT = useTranslations("Navbar");
  const locale = useLocale();
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);

  const pathname = usePathname();
  const isEnglish = useMemo(() => getBaseLocale(locale) === "en", [locale]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const handleLogout = useCallback(() => {
    signOut({ callbackUrl: getPathWithLocale("/", locale) });
  }, [locale]);

  const handleLoginClick = useCallback(() => {
    setShowLoginPopup(true);
  }, []);

  const switchToRegister = useCallback(() => {
    setShowLoginPopup(false);
    setShowRegisterPopup(true);
  }, []);

  const switchToLogin = useCallback(() => {
    setShowRegisterPopup(false);
    setShowLoginPopup(true);
  }, []);

  const dashboardHref =
    session?.user?.role === "admin"
      ? "/admin"
      : getPathWithLocale("/dashboard", locale);

  return (
    <>
      {/* Прозрачная шапка: только лого + войти + язык */}
      <header
        className="pointer-events-none fixed top-0 left-0 right-0 z-[100] bg-transparent"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto max-w-7xl px-4">
          <div className="nav-in flex h-14 items-center justify-end">
            <div className="pointer-events-auto flex items-center gap-2 md:gap-3">
              {/* Авторизация */}
              {session?.user ? (
                <div className="hidden items-center gap-2 md:flex">
                  <Link
                    href={dashboardHref}
                    className="rounded-full bg-[#2f73f2] px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  >
                    {session.user.role === "admin"
                      ? isEnglish
                        ? "Admin panel"
                        : "Админка"
                      : isEnglish
                        ? "Dashboard"
                        : "Кабинет"}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="rounded-full border border-white/30 bg-white/10 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/20"
                  >
                    {commonT("logout")}
                  </button>
                </div>
              ) : null}

              {/* Гамбургер — только мобайл */}
              <button
                className="text-white md:hidden"
                onClick={() => setOpen((v) => !v)}
                aria-label={navbarT("menu")}
                aria-expanded={open}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16m-16 6h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <SideMenu
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClose={() => setOpen(false)}
        onLogin={handleLoginClick}
      />

      <LangSwitcher />

      {/* Вход — иконка (только неавторизован), нижний левый угол, напротив языка и лого */}
      {!session?.user && (
        <button
          type="button"
          onClick={handleLoginClick}
          aria-label={commonT("login")}
          title={commonT("login")}
          className="fixed top-6 right-[5vw] z-[90] hidden translate-x-1/2 h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white/60 transition-all duration-200 hover:border-white/50 hover:bg-white/5 hover:text-white md:flex"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        </button>
      )}

      {/* Auth Popups */}
      <LoginPopup
        isOpen={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        onSwitchToRegister={switchToRegister}
      />
      <RegisterPopup
        isOpen={showRegisterPopup}
        onClose={() => setShowRegisterPopup(false)}
        onSwitchToLogin={switchToLogin}
      />
    </>
  );
}